import { Box, FormControl, Grid, MenuItem, Select } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import * as Sentry from '@sentry/react'
import { ExistingStockPresenter } from 'components/domain/addArticles/AddArticleDock/ExistingStock/ExistingStockPresenter'
import { GradingPanel } from 'components/domain/stockPricing/LocationsPanel/LocationsPanelRow'
import { LanguageFlag } from 'components/facets/LanguageFlag'
import { useUser } from 'components/providers/UserProvider'
import { useAppContext, useHotkeyConfig } from 'lib/hooks'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CardLanguage,
  countryEditionMap,
  FINISH_TYPES_CONVERSIONS,
  FINISH_TYPE_LABELS,
  flagCode,
  IdGame,
  isSingle,
  RichArticle,
  Template,
} from 'shared'
import { SetState } from 'types'
import {
  COLD_FOIL_ICON,
  EXTRA_ATTRIBUTES_VALUES_AND_IMAGES_MAP,
  FIRST_EDITION_ICON,
  FOIL_ICON,
  PLAYSET_ICON,
  RAINBOW_FOIL_ICON,
  REVERSE_HOLO_ICON,
  SIGNED_ICON,
} from 'utils/constants'
import { Condition } from './Condition'

interface HotkeyLegendProps {
  addByExp?: boolean
  disabled?: boolean
  article: RichArticle
  onClick: () => void
  setInputs: SetState<RichArticle>
  showExistingStock: boolean
  template: Template
  setDisabledCommentTextfield: SetState<boolean>
  onGradingModeChange?: (active: boolean) => void
  setArticleIsGraded: SetState<boolean>
  set: (attribute: keyof Template, value: string | boolean | number) => void
  onRefocus?: () => void
}

interface HotkeyProps {
  hotkey: string[]
}

const keyToKeyCode = (key: string): number => {
  if (key.length === 1) return key.toUpperCase().charCodeAt(0)
  else if (key.toLowerCase() === 'del') return 46
  else if (key.toLowerCase() === 'esc') return 46
  else if (key.toLowerCase() === 'enter') return 13
  else if (key.toLowerCase() === 'spacebar') return 13
  else if (key.toLowerCase() === 'enter / spacebar') return 13
  return 0
}

const resolveHotkey = (attribute: string, value: string): [string, any] => {
  if (attribute === 'language') {
    const enumKey = Object.entries(CardLanguage).find(([_, v]) => v === value)?.[0]

    if (enumKey && !isNaN(Number(enumKey))) {
      return ['idLanguage', Number(enumKey)]
    }
  }

  return [attribute, value]
}

const SIZE = 16

export const HotkeyLegend = ({
  addByExp,
  disabled,
  onClick,
  article,
  showExistingStock,
  setInputs,
  template,
  setDisabledCommentTextfield,
  onGradingModeChange,
  setArticleIsGraded,
  set,
  onRefocus,
}: HotkeyLegendProps): JSX.Element => {
  const { isMobile } = useAppContext()
  const { t } = useTranslation()
  const { activeGame } = useUser()
  const [gradingMode, setGradingMode] = useState(false)
  const [finishDropdownUsed, setFinishDropdownUsed] = useState(false)
  const gradingCompany = article.gradingCompany ?? ''
  const cumulativeGrade = article.grade ?? ''

  const isSealedArticle = !isSingle(article.card)

  // only the finish types enabled for this stock type can be set on the article
  let finishTypesPerActiveGame = (
    EXTRA_ATTRIBUTES_VALUES_AND_IMAGES_MAP.find((entry) => entry.idGame === activeGame.idGame)
      ?.finishType ?? []
  )
    .filter((finish) => (isSealedArticle ? finish.sealed : finish.singles))
    .map((finish) => finish.label)
  if (activeGame.idGame === IdGame.Pokemon)
    finishTypesPerActiveGame = finishTypesPerActiveGame.filter(
      (f) => f !== FINISH_TYPE_LABELS.REGULAR && f !== FINISH_TYPE_LABELS.REVERSE_HOLO
    )

  // usati per leggere la dimensione di hotkey prima che si apra il grading panel
  const legendRef = useRef<HTMLDivElement>(null)
  const [legendWidth, setLegendWidth] = useState<number | null>(null)

  useEffect(() => {
    if (legendRef.current) {
      setLegendWidth(legendRef.current.offsetWidth)
    }
    if (onGradingModeChange) {
      onGradingModeChange(gradingMode)
    }
  }, [gradingMode, onGradingModeChange])

  useEffect(() => {
    if (article.gradingCompany !== undefined && article.gradingCompany !== '') {
      setArticleIsGraded(true)
    }
  }, [article.gradingCompany])

  const defaultHotkeys = [
    ['grading', 'grading', 'grading'],
    ['c', t('addArticles.hotkeys.create&copy'), 'create&copy'],
    // ['del', t('addArticles.hotkeys.back'), 'back'],
    ['enter / spacebar', t('addArticles.hotkeys.confirm'), 'confirm'],

    ['esc', t('addArticles.hotkeys.cancel'), 'cancel'],
  ]

  const { palette } = useTheme()

  if (addByExp)
    defaultHotkeys.push(
      ...[
        ['v', 'prev', 'prev'],
        ['b', 'next', 'next'],
      ]
    )

  const hotkeys = [...defaultHotkeys, ...useHotkeyConfig()]

  const Hotkey = ({ hotkey }: HotkeyProps): JSX.Element | null => {
    if (!hotkey) return null
    const [, attribute, value] = hotkey
    const [isPressed, setPressed] = useState(false)
    const Icon = ({ src }: { src: string }): JSX.Element => (
      <img
        alt="icon"
        style={{ display: 'block', margin: '0 auto' }}
        height={`${SIZE * 1.5}px`}
        src={src}
      />
    )

    const isActive = (() => {
      if (attribute === 'language') {
        const expectedLanguageCode = flagCode(CardLanguage[value as keyof typeof CardLanguage])
        const articleLanguageCode = flagCode(
          CardLanguage[article.languageName as keyof typeof CardLanguage]
        )

        return expectedLanguageCode === articleLanguageCode
      }
      if (attribute === 'condition') {
        return article.condition === value
      }

      if (attribute === 'countryEdition') {
        const match = countryEditionMap.find(
          (entry) => entry.label.toLowerCase() === value.toLowerCase()
        )

        return match?.value === article.countryEdition
      }

      if (attribute === 'grading') {
        return article.gradingCompany !== '' && article.gradingCompany !== undefined
      }

      if (attribute === 'isFoil') {
        return article.finishType === FINISH_TYPE_LABELS.FOIL
      }

      if (attribute === 'isRainbowFoil') {
        return article.finishType === FINISH_TYPE_LABELS.RAINBOW_FOIL
      }
      if (attribute === 'isColdFoil') {
        return article.finishType === FINISH_TYPE_LABELS.COLD_FOIL
      }

      if (attribute === 'isReverseHolo') {
        return article.finishType === FINISH_TYPE_LABELS.REVERSE_HOLO
      }

      if (attribute in article) {
        return article[attribute as keyof RichArticle]
      }
      return false
    })()

    const Legend = (): JSX.Element => {
      switch (attribute) {
        case 'condition':
          return <Condition id="HotkeyLegend-condition" value={value} />
        case 'language':
          return (
            <LanguageFlag
              style={{ padding: '0px 5px' }}
              height={SIZE}
              code={flagCode(CardLanguage[value as keyof typeof CardLanguage]) || 'gg'}
              data-testid={`${flagCode(CardLanguage[value as keyof typeof CardLanguage])}-flag`}
            />
          )
        case 'isFoil':
          return <Icon src={FOIL_ICON} />
        case 'isPlayset':
          return <Icon src={PLAYSET_ICON} />
        case 'isSigned':
          return <Icon src={SIGNED_ICON} />
        case 'isFirstEd':
          return <Icon src={FIRST_EDITION_ICON} />
        case 'isReverseHolo':
          return <Icon src={REVERSE_HOLO_ICON} />
        case 'isRainbowFoil':
          return <Icon src={RAINBOW_FOIL_ICON} />
        case 'isColdFoil':
          return <Icon src={COLD_FOIL_ICON} />

        case 'grading':
          return <i data-testid="grading" className="fa-solid fa-fire"></i>

        case 'countryEdition':
          return (
            <LanguageFlag
              style={{ padding: '0px 5px' }}
              height={SIZE}
              code={value.toLowerCase()}
              data-testid={`${value}-flag`}
            />
          )

        default:
          return (
            <Box
              style={{
                fontSize: `${SIZE}PX`,
                fontWeight: 300,
                fontStyle: 'italic',
              }}
            >
              {attribute}
            </Box>
          )
      }
    }

    return (
      <Box
        minWidth="34px"
        padding="0 2px"
        onMouseUp={() => {
          if (attribute === 'grading') {
            setGradingMode(true)
            return
          }

          const [attr, val] = resolveHotkey(attribute, value)

          set(attr as keyof Template, val)

          setPressed(false)
          const keyEventConfig = {
            bubbles: true,
            keyCode: keyToKeyCode(hotkey[0]),
          }

          const event = new KeyboardEvent('keydown', keyEventConfig)
          document.activeElement?.dispatchEvent(event)
          const event2 = new KeyboardEvent('keyup', keyEventConfig)
          document.activeElement?.dispatchEvent(event2)
        }}
        onMouseDown={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (!disabled) setPressed(true)
        }}
        onMouseLeave={() => {
          setPressed(false)
        }}
        style={{
          border: isPressed ? '1px solid grey' : `2px solid ${palette.primary.main}`,
          borderRadius: '5px',
          margin: `${SIZE / 8}px`,
          padding: `${SIZE / 4}px`,
          fontSize: `${SIZE}px`,
          minWidth: `${SIZE * 3}px`,
          color: isActive ? 'white' : palette.primary.main,
          cursor: 'pointer',
          backgroundColor: isActive ? palette.active : 'white',
          boxShadow: '0px 4px 8px -2px rgba(0,0,0,0.5)',
        }}
      >
        <Box>{<Legend />}</Box>
        <Box textAlign="center" fontWeight="bold">
          {hotkey[0].toUpperCase()}
        </Box>
      </Box>
    )
  }

  const conditionHotkeys = hotkeys.filter(([_, attr]) => attr === 'condition')
  const languageHotkeys = hotkeys.filter(([_, attr]) => attr === 'language')
  const countryHotkeys = hotkeys.filter(([_, attr]) => attr === 'countryEdition')
  const toggleHotkeys = hotkeys.filter(([_, attr]) => attr.startsWith('is'))
  const excludedAttributes = [
    'condition',
    'language',
    'countryEdition',
    'grading',
    'isFirstEd',
    'isSigned',
    'isFoil',
    'isRainbowFoil',
    'isColdFoil',
    'isReverseHolo',
    'isPlayset',
  ]
  const otherHotkeys = hotkeys.filter(([, attr]) => !excludedAttributes.includes(attr))
  otherHotkeys

  const handleNewGrading = (newGrading: { gradingCompany: string; grade: string }) => {
    setInputs(
      (prev) =>
        new RichArticle({
          ...prev.toPlainObject(),
          grade: newGrading.grade,
          gradingCompany: newGrading.gradingCompany,
        })
    )
    setGradingMode(false)
  }

  const canSetOtherFinishTypes =
    activeGame.idGame === IdGame.Pokemon && finishTypesPerActiveGame.length > 0

  const pokemonFinishDropdown = canSetOtherFinishTypes && (
    <Box display="flex" alignItems="center" justifyContent="center" marginY={2}>
      <p className="fw-semibold fs-6 m-0 p-0 me-2" style={{ whiteSpace: 'nowrap' }}>
        Other finish types
      </p>
      <FormControl style={{ minWidth: 160 }}>
        <Select
          name="other-finish"
          data-testid="other-finish-select"
          value={article.finishType}
          onClose={() => {
            // restituisci il focus alla quantity così Enter aggiunge la carta
            setTimeout(() => onRefocus?.(), 0)
          }}
          onChange={(e) => {
            const newValue = e.target.value as string
            const selectedFinish = FINISH_TYPES_CONVERSIONS.find(
              (entry) => entry.label === newValue
            )
            if (!selectedFinish) {
              Sentry.withScope((scope) => {
                scope.setTag('component', 'HotkeyLegend')
                scope.setExtra('context', 'Finish type not found in FINISH_TYPES_CONVERSIONS')
                scope.setExtra('newValue', newValue)
                Sentry.captureMessage('Unknown finish type selected in HotkeyLegend')
              })
              return
            }
            set('finishType', selectedFinish.label)
            setFinishDropdownUsed(true)
          }}
        >
          <MenuItem key={FINISH_TYPE_LABELS.REGULAR} value={FINISH_TYPE_LABELS.REGULAR}>
            {'-'}
          </MenuItem>
          {finishTypesPerActiveGame.map((finish) => (
            <MenuItem key={finish} value={finish}>
              {finish}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  )

  const hotkeyGrid = (
    <Grid container>
      <Grid item xs={12}>
        <Box style={{ opacity: disabled && !finishDropdownUsed ? 0.2 : 'initial' }}>
          {!isSealedArticle && (
            <Box display="flex" flexWrap="wrap" sx={{ justifyContent: 'center' }}>
              {conditionHotkeys.map((hkey) => (
                <Hotkey key={hkey[0]} hotkey={hkey} />
              ))}
            </Box>
          )}
          <Box className="d-flex flex-wrap justify-content-center my-3">
            {languageHotkeys.map((hkey) => (
              <Hotkey key={hkey[0]} hotkey={hkey} />
            ))}
          </Box>
          {(() => {
            const visibleToggles = isSealedArticle
              ? toggleHotkeys.filter(([, attr]) => activeGame.sealedStockHeaders.includes(attr))
              : toggleHotkeys
            return (
              visibleToggles.length > 0 && (
                <Box display="flex" flexWrap="wrap" sx={{ justifyContent: 'center' }}>
                  {visibleToggles.map((hkey) => (
                    <Hotkey key={hkey[0]} hotkey={hkey} />
                  ))}
                </Box>
              )
            )
          })()}
          <Box display="flex" flexWrap="wrap" sx={{ justifyContent: 'center' }}>
            {countryHotkeys.map((hkey) => (
              <Hotkey key={hkey[0]} hotkey={hkey} />
            ))}
          </Box>
          <Box display="flex" flexWrap="wrap" sx={{ justifyContent: 'center' }}>
            <Hotkey hotkey={hotkeys.find((hkey) => hkey[1] === 'grading')!} />
            {isMobile && otherHotkeys.map((hkey) => <Hotkey key={hkey[0]} hotkey={hkey} />)}
          </Box>
          {pokemonFinishDropdown}
          {!isMobile && (
            <Box display="flex" flexWrap="wrap" sx={{ justifyContent: 'center' }}>
              {otherHotkeys.map((hkey) => (
                <Hotkey key={hkey[0]} hotkey={hkey} />
              ))}
            </Box>
          )}
          <Box display="flex" sx={{ justifyContent: 'center' }}>
            <Box margin={2}>
              <p className={isMobile ? 'm-0 p-0' : ''}>{t('addArticles.hotkeys.titleExplain')}</p>
              <p className={isMobile ? 'm-0 p-0' : ''}>{t('addArticles.hotkeys.tab')}</p>
            </Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  )

  return (
    <div ref={legendRef}>
      <Box data-testid="hotkey-legend" textAlign="center" boxSizing="border-box" onClick={onClick}>
        {isMobile && gradingMode ? (
          <div
            style={{ width: legendWidth || 'auto', height: '240px' }}
            className="mt-1 container-fluid"
          >
            <p>{t('addArticles.gradedCardsListing')}</p>
            <GradingPanel
              setOpenModifyGradingPanel={setGradingMode}
              handleNewGrading={handleNewGrading}
              initialGrade={cumulativeGrade}
              initialGradingCompany={gradingCompany}
            />
          </div>
        ) : (
          hotkeyGrid
        )}
        {gradingMode && !isMobile && (
          <div
            style={{ width: legendWidth || 'auto', height: '240px' }}
            className="mt-2 container-fluid"
          >
            <p>{t('addArticles.gradedCardsListing')}</p>
            <GradingPanel
              setOpenModifyGradingPanel={setGradingMode}
              handleNewGrading={handleNewGrading}
              initialGrade={cumulativeGrade}
              initialGradingCompany={gradingCompany}
            />
          </div>
        )}
        {showExistingStock && !gradingMode && !isMobile && (
          <Box marginTop={1}>
            <ExistingStockPresenter
              setInputs={setInputs}
              article={article}
              template={template}
              setDisabledCommentTextfield={setDisabledCommentTextfield!}
            />
          </Box>
        )}
      </Box>
    </div>
  )
}
